import React, { useState } from 'react';
import { Tooltip, Typography, Box, Button, TooltipProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { InfoOutlined } from '@mui/icons-material';

const infoColor = '#48bff7';

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: infoColor,
    color: theme.palette.common.white,
    maxWidth: '15em',
    fontSize: '1em',
    borderRadius: '0.5em',
    fontWeight: 'bold',
    fontFamily: 'Inter',
    padding: '1em 1.5em',
  },
  [`& .MuiTooltip-arrow`]: {
    '&:before': {
      border: `1px solid ${infoColor}`,
    },
    color: infoColor,
  },
}));

export { HtmlTooltip };

interface TooltipVariantProps {
  title: string;
  open: boolean;
  iconColor: string;
  placement?: TooltipProps['placement'];
  handleTooltipOpen: () => void;
  handleTooltipClose: () => void;
}

const TooltipVariant = ({
  title,
  open,
  iconColor,
  placement,
  handleTooltipOpen,
  handleTooltipClose,
}: TooltipVariantProps) => {
  return (
    <HtmlTooltip
      placement={placement || 'top-start'}
      onClose={handleTooltipClose}
      open={open}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      arrow
      title={
        <Box>
          <Typography variant='body2'>{title}</Typography>
        </Box>
      }
    >
      <Button
        onClick={handleTooltipOpen}
        variant='text'
        aria-label='More information'
        sx={{ color: iconColor, fontSize: '0.8rem', cursor: 'pointer', minWidth: '0em' }}
      >
        <InfoOutlined />
      </Button>
    </HtmlTooltip>
  );
};

interface CustomTooltipProps {
  title: string;
  placement?: TooltipProps['placement'];
  variant?: 'click' | 'hover';
}

const CustomTooltip = ({ title, placement, variant = 'click' }: CustomTooltipProps) => {
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => setOpen(false);
  const handleTooltipOpen = () => setOpen(true);

  const tooltipVariant = (
    <TooltipVariant
      title={title}
      open={open}
      iconColor={variant === 'click' ? '#8460CB' : infoColor}
      placement={placement}
      handleTooltipOpen={handleTooltipOpen}
      handleTooltipClose={handleTooltipClose}
    />
  );

  return variant === 'click' ? (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Box>{tooltipVariant}</Box>
    </ClickAwayListener>
  ) : (
    <Box onMouseEnter={handleTooltipOpen} onMouseLeave={handleTooltipClose}>
      {tooltipVariant}
    </Box>
  );
};

export default CustomTooltip;